import { Injectable } from "@angular/core";
import {
  Actions,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import {
  LocalConferenceStreamActions,
  RemoteConferenceStreamActions
} from "@youm-front/conference-stream/data-access";
import {
  RoomBrandBgDeferredErrorVO,
  RoomBrandBgDeleteEventVO,
  RoomBrandBgSetEventVO,
  RoomBrandCaptionLowerThirdDeferredErrorVO,
  RoomBrandCaptionLowerThirdDeleteEventVO,
  RoomBrandCaptionLowerThirdSetEventVO,
  RoomBrandLogoDeferredErrorVO,
  RoomBrandLogoDeleteEventVO,
  RoomBrandLogoSetEventVO,
  RoomBrandOverlayDeferredErrorVO,
  RoomBrandOverlayDeleteEventVO,
  RoomBrandOverlaySetEventVO,
  RoomStageLayoutDeferredErrorVO,
  RoomStageLayoutStreamsSetEventVO,
  RoomStreamAudioUpdateCmd,
  RoomStreamDeleteCmd,
  RoomStreamRestartCmd,
  RoomStreamToSubscribeVO,
  RoomStreamUpdateEventVO
} from "@youm-front/room/domain";
import { Observable } from "rxjs";
import {
  concatMap,
  debounceTime,
  exhaustMap,
  filter,
  map,
  mergeMap,
  takeUntil,
  tap
} from "rxjs/operators";
import { RoomActions } from "../actions";
import { RoomEvents, RoomStageLayoutEvents, RoomStreamEvents } from "../events";
import {
  RoomBrandBgFacade,
  RoomBrandCaptionLowerThirdFacade,
  RoomBrandLogoFacade,
  RoomBrandOverlayFacade,
  RoomContextFacade,
  RoomStageLayoutFacade
} from "../facades";
import { RoomService } from "../services";

@Injectable()
export class RoomEventsEffects implements OnRunEffects {
  private static ROOM_STAGE_LAYOUT_STREAMS_SET_EVENT_DEBOUNCE_TIME_MS = 100;

  constructor(
    private readonly _actions$: Actions,
    private readonly _roomService: RoomService,
    private readonly _roomBrandBgFacade: RoomBrandBgFacade,
    private readonly _roomBrandCaptionLowerThirdFacade: RoomBrandCaptionLowerThirdFacade,
    private readonly _roomBrandLogoFacade: RoomBrandLogoFacade,
    private readonly _roomBrandOverlayFacade: RoomBrandOverlayFacade,
    private readonly _roomContextFacade: RoomContextFacade,
    private readonly _roomStageLayoutFacade: RoomStageLayoutFacade
  ) {}

  listenRoomStreamEvent$ = createEffect(() =>
    this._roomService.listenRoomStreamEvent().pipe(
      // Ignore room stream with the same roomContextId,
      // the local instance should be used
      filter(
        (_) => _.data.roomContextId !== this._roomContextFacade.roomContextId
      ),
      mergeMap((stream) => [
        RoomEvents.remoteRoomStream({
          roomStreamToSubscribeVO: new RoomStreamToSubscribeVO(stream.data)
        })
      ])
    )
  );

  listenRoomStreamDeleteEvent$ = createEffect(() =>
    this._roomService.listenRoomStreamDeleteEvent().pipe(
      mergeMap((_) => [
        RoomStreamEvents.roomStreamDeleteEvent({
          roomStreamDeleteCmd: new RoomStreamDeleteCmd(_.data)
        })
      ])
    )
  );

  listenRoomStreamRestartEvent$ = createEffect(() =>
    this._roomService.listenRoomStreamRestartEvent().pipe(
      mergeMap((_) => [
        RoomStreamEvents.roomStreamRestartEvent({
          roomStreamRestartCmd: new RoomStreamRestartCmd(_.data)
        })
      ])
    )
  );

  listenRoomStreamAudioUpdateEvent$ = createEffect(() =>
    this._roomService.listenRoomStreamAudioUpdateEvent().pipe(
      mergeMap((_) => [
        RoomStreamEvents.roomStreamAudioUpdateEvent({
          roomStreamAudioUpdateCmd: new RoomStreamAudioUpdateCmd(_.data)
        })
      ])
    )
  );

  remoteRoomStream$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomEvents.remoteRoomStream),
      map((_) =>
        RemoteConferenceStreamActions.streamAdd({
          id: _.roomStreamToSubscribeVO.value.streamId,
          kinds: _.roomStreamToSubscribeVO.value.streamKinds,
          credentials: _.roomStreamToSubscribeVO.value.credentials,
          roomContextId: this._roomContextFacade.roomContextId
        })
      )
    )
  );

  roomStreamDeleteEvent$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomStreamEvents.roomStreamDeleteEvent),
      mergeMap((_) => [
        LocalConferenceStreamActions.streamDelete({
          id: _.roomStreamDeleteCmd.value.streamId
        }),
        RemoteConferenceStreamActions.streamDelete({
          id: _.roomStreamDeleteCmd.value.streamId
        })
      ])
    )
  );

  roomStreamRestartEvent$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomStreamEvents.roomStreamRestartEvent),
        tap((_) =>
          this._roomService.roomStreamLocalRestart(_.roomStreamRestartCmd)
        )
      ),
    { dispatch: false }
  );

  roomStreamAudioUpdateEvent$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomStreamEvents.roomStreamAudioUpdateEvent),
        concatMap((action) =>
          this._roomService.roomStreamLocalAudioUpdate(
            action.roomStreamAudioUpdateCmd
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandCaptionLowerThirdSetEvent$ = createEffect(
    () =>
      this._roomService.listenRoomBrandCaptionLowerThirdSet().pipe(
        tap((_) =>
          this._roomBrandCaptionLowerThirdFacade.roomBrandCaptionLowerThirdSetEvent(
            new RoomBrandCaptionLowerThirdSetEventVO({
              roomId: _.data.roomId,
              caption: _.data.caption
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandCaptionLowerThirdDeleteEvent$ = createEffect(
    () =>
      this._roomService.listenRoomBrandCaptionLowerThirdDelete().pipe(
        tap((_) =>
          this._roomBrandCaptionLowerThirdFacade.roomBrandCaptionLowerThirdDeleteEvent(
            new RoomBrandCaptionLowerThirdDeleteEventVO({
              roomId: _.data.roomId
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandCaptionLowerThirdDeferredErrorEvent$ = createEffect(
    () =>
      this._roomService
        .listenRoomBrandCaptionLowerThirdDeferredError()
        .pipe(
          tap((_) =>
            this._roomBrandCaptionLowerThirdFacade.roomBrandCaptionLowerThirdDeferredErrorEvent(
              new RoomBrandCaptionLowerThirdDeferredErrorVO(_.data)
            )
          )
        ),
    { dispatch: false }
  );

  listenRoomBrandBgSet$ = createEffect(
    () =>
      this._roomService.listenRoomBrandBgSet().pipe(
        tap((_) =>
          this._roomBrandBgFacade.roomBrandBgSetEvent(
            new RoomBrandBgSetEventVO({
              roomId: _.data.roomId,
              brandBg: _.data.brandBg
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandBgDelete$ = createEffect(
    () =>
      this._roomService.listenRoomBrandBgDelete().pipe(
        tap((_) =>
          this._roomBrandBgFacade.roomBrandBgDeleteEvent(
            new RoomBrandBgDeleteEventVO({
              roomId: _.data.roomId
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandBgDeferredError$ = createEffect(
    () =>
      this._roomService
        .listenRoomBrandBgDeferredError()
        .pipe(
          tap((_) =>
            this._roomBrandBgFacade.roomBrandBgDeferredErrorEvent(
              new RoomBrandBgDeferredErrorVO(_.data)
            )
          )
        ),
    { dispatch: false }
  );

  listenRoomBrandLogoSet$ = createEffect(
    () =>
      this._roomService.listenRoomBrandLogoSet().pipe(
        tap((_) =>
          this._roomBrandLogoFacade.roomBrandLogoSetEvent(
            new RoomBrandLogoSetEventVO({
              roomId: _.data.roomId,
              brandLogo: _.data.brandLogo
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandLogoDelete$ = createEffect(
    () =>
      this._roomService.listenRoomBrandLogoDelete().pipe(
        tap((_) =>
          this._roomBrandLogoFacade.roomBrandLogoDeleteEvent(
            new RoomBrandLogoDeleteEventVO({
              roomId: _.data.roomId
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandLogoDeferredError$ = createEffect(
    () =>
      this._roomService
        .listenRoomBrandLogoDeferredError()
        .pipe(
          tap((_) =>
            this._roomBrandLogoFacade.roomBrandLogoDeferredErrorEvent(
              new RoomBrandLogoDeferredErrorVO(_.data)
            )
          )
        ),
    { dispatch: false }
  );

  listenRoomBrandOverlaySet$ = createEffect(
    () =>
      this._roomService.listenRoomBrandOverlaySet().pipe(
        tap((_) =>
          this._roomBrandOverlayFacade.roomBrandOverlaySetEvent(
            new RoomBrandOverlaySetEventVO({
              roomId: _.data.roomId,
              brandOverlay: _.data.brandOverlay
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandOverlayDelete$ = createEffect(
    () =>
      this._roomService.listenRoomBrandOverlayDelete().pipe(
        tap((_) =>
          this._roomBrandOverlayFacade.roomBrandOverlayDeleteEvent(
            new RoomBrandOverlayDeleteEventVO({
              roomId: _.data.roomId
            })
          )
        )
      ),
    { dispatch: false }
  );

  listenRoomBrandOverlayDeferredError$ = createEffect(
    () =>
      this._roomService
        .listenRoomBrandOverlayDeferredError()
        .pipe(
          tap((_) =>
            this._roomBrandOverlayFacade.roomBrandOverlayDeferredErrorEvent(
              new RoomBrandOverlayDeferredErrorVO(_.data)
            )
          )
        ),
    { dispatch: false }
  );

  listenRoomStageLayoutDeferredError$ = createEffect(
    () =>
      this._roomService
        .listenRoomStageLayoutDeferredError()
        .pipe(
          tap((_) =>
            this._roomStageLayoutFacade.roomStageLayoutDeferredErrorEvent(
              new RoomStageLayoutDeferredErrorVO(_.data)
            )
          )
        ),
    { dispatch: false }
  );

  listenRoomStageLayoutStreamsSet$ = createEffect(() =>
    this._roomService.listenRoomStageLayoutStreamsSet().pipe(
      // May receive several updates in row.
      // Try to use the last one in a batch to provide a better UI experience.
      debounceTime(
        RoomEventsEffects.ROOM_STAGE_LAYOUT_STREAMS_SET_EVENT_DEBOUNCE_TIME_MS
      ),
      mergeMap((_) => [
        RoomStageLayoutEvents.roomStageLayoutStreamsSetEvent({
          roomStageLayoutStreamsSetEventVO:
            new RoomStageLayoutStreamsSetEventVO({
              layout: _.data.layout,
              roomId: _.data.roomId,
              videos: _.data.videos,
              audios: _.data.audios,
              focus: _.data.focus,
              main: _.data.main
            })
        })
      ])
    )
  );

  listenRoomStreamUpdateEvent$ = createEffect(() =>
    this._roomService.listenRoomStreamUpdateEvent().pipe(
      mergeMap((_) => [
        RoomEvents.roomStreamUpdate({
          roomStreamUpdateEventVO: new RoomStreamUpdateEventVO(_.data)
        })
      ])
    )
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this._actions$.pipe(
      ofType(RoomActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this._actions$.pipe(ofType(RoomActions.destroy)))
        )
      )
    );
  }
}
